import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";

// Pins the viewport and slides a row of panels sideways as the user scrolls down.
// Once the last panel is fully in view, the pin releases and the page scrolls normally.
// About one screen of scrolling drives the whole slide, and a scroll that stops
// midway settles to whichever end it was heading toward.
const HOLD = 0.05; // fraction of the scroll spent resting at the start and end
const SNAP_DELAY = 180; // ms of scroll silence before settling
// After the slide, the pin holds for this share of a viewport while the next section
// slides up over it as a sheet. The page must pull that section up by the same amount.
export const EXIT_OVERLAP_VH = 100; // a full screen, so the sheet fully covers the view before the pin releases

const HorizontalIntro = ({ panels }) => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [distance, setDistance] = useState(0);
  const [viewport, setViewport] = useState(() => window.innerHeight);

  useLayoutEffect(() => {
    const measure = () => {
      setDistance(Math.max(0, trackRef.current.scrollWidth - window.innerWidth));
      setViewport(window.innerHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(trackRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Vertical scroll budget: one viewport for the slide, however far the panels travel,
  // plus the exit overlap while the next section covers the pinned view.
  const budget = distance > 0 ? viewport : 0;
  const overlap = budget ? (viewport * EXIT_OVERLAP_VH) / 100 : 0;
  const total = budget + overlap;
  const height = viewport + total;
  const s = total ? budget / total : 1; // share of progress used by the sideways slide

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  const rawX = useTransform(scrollYProgress, [s * HOLD, s * (1 - HOLD)], [0, -distance], { clamp: true });
  const x = useSpring(rawX, { stiffness: 140, damping: 30, mass: 0.35 });

  // The first panel eases back slightly as it slides away, like a card pushed across a desk.
  const firstScale = useTransform(scrollYProgress, [s * HOLD, s * 0.7], [1, 0.94]);
  const firstOpacity = useTransform(scrollYProgress, [s * HOLD, s * 0.7], [1, 0.35]);
  const hintOpacity = useTransform(scrollYProgress, [0, s * HOLD], [1, 0]);
  const barScale = useTransform(scrollYProgress, [0, s], [0, 1], { clamp: true });

  // Exit: the pinned view sinks back and darkens as the next section rises over it.
  const exitScale = useTransform(scrollYProgress, [s, 1], [1, 0.92], { clamp: true });
  const exitY = useTransform(scrollYProgress, [s, 1], ["0%", "-3%"], { clamp: true });
  const exitRadius = useTransform(scrollYProgress, [s, 1], [0, 36], { clamp: true });
  const shade = useTransform(scrollYProgress, [s, 1], [0, 0.4], { clamp: true });

  useEffect(() => {
    if (!budget) return;
    let lastY = window.scrollY;
    let direction = 0;
    let timer;
    const onScroll = () => {
      const y = window.scrollY;
      if (y !== lastY) direction = y > lastY ? 1 : -1;
      lastY = y;
      clearTimeout(timer);
      timer = setTimeout(() => {
        const top = sectionRef.current.offsetTop;
        const into = window.scrollY - top;
        if (into <= 2 || into >= budget - 2) return;
        const p = into / budget;
        // Commit in the direction of travel once you're a little way in; otherwise go to the nearest end.
        let toEnd = p > 0.5;
        if (direction > 0 && p > 0.15) toEnd = true;
        if (direction < 0 && p < 0.85) toEnd = false;
        window.scrollTo({ top: toEnd ? top + budget : top, behavior: "smooth" });
      }, SNAP_DELAY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [budget]);

  return (
    <section ref={sectionRef} className="relative" style={{ height }}>
      <div className="sticky top-0 h-screen overflow-hidden">
        <motion.div
          style={{ scale: exitScale, y: exitY, borderRadius: exitRadius }}
          className="relative h-full w-full overflow-hidden origin-[50%_30%] will-change-transform"
        >
        <motion.div ref={trackRef} style={{ x }} className="flex h-full w-max will-change-transform">
          {panels.map(({ key, width = "w-screen", className = "", content }, i) => (
            <motion.div
              key={key}
              className={`relative h-full shrink-0 ${width} ${className}`}
              style={i === 0 ? { scale: firstScale, opacity: firstOpacity } : undefined}
            >
              {content}
            </motion.div>
          ))}
        </motion.div>
        <motion.div style={{ opacity: shade }} className="absolute inset-0 bg-black pointer-events-none" />
        </motion.div>

        <motion.div
          style={{ opacity: hintOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ink/50 pointer-events-none"
        >
          scroll
          <motion.span animate={{ x: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
            →
          </motion.span>
        </motion.div>

        <div className="absolute bottom-0 inset-x-0 h-[3px] bg-ink/5">
          <motion.div style={{ scaleX: barScale }} className="h-full bg-accent origin-left" />
        </div>
      </div>
    </section>
  );
};

export default HorizontalIntro;
